import type { Diagnosis, Procedure } from "@/types/classification";

export interface ProcedureWithDisplayFlags extends Procedure {
  isExpected: boolean;
  isPresent: boolean;
  isPositionCorrect: boolean | null; // This only applies to body system codes.
}

export const getAllProcedures = (args: {
  expectedProcedures: Procedure[];
  runProcedures: Procedure[];
}): ProcedureWithDisplayFlags[] => {
  const expectedProcedures = [...args.expectedProcedures];
  const runProcedures = [...args.runProcedures];

  const outputValues = [];
  let loopIndex = 0;

  while (loopIndex < args.expectedProcedures.length + args.runProcedures.length) {
    const expectedProcedure = expectedProcedures[0];
    const runProcedure = runProcedures[0];

    if (!expectedProcedure && !runProcedure) {
      break;
    }

    if (runProcedure && !expectedProcedure) {
      outputValues.push({
        ...runProcedure,
        isExpected: false,
        isPresent: true,
        isPositionCorrect: false,
      });
      runProcedures.splice(0, 1);
    } else if (expectedProcedure && !runProcedure) {
      outputValues.push({
        ...expectedProcedure,
        isExpected: true,
        isPresent: false,
        isPositionCorrect: false,
      });
      expectedProcedures.splice(0, 1);
    } else if (expectedProcedure.code === runProcedure.code) {
      // We can track the original positions by keeping track of how many items we've removed from each list
      const expectedItemsRemoved = args.expectedProcedures.length - expectedProcedures.length;
      const runItemsRemoved = args.runProcedures.length - runProcedures.length;

      // The current position in the original lists
      const originalExpectedPosition = expectedItemsRemoved;
      const originalRunPosition = runItemsRemoved;

      outputValues.push({
        ...runProcedure,
        isExpected: true,
        isPresent: true,
        isPositionCorrect: originalExpectedPosition === originalRunPosition,
      });
      runProcedures.splice(0, 1);
      expectedProcedures.splice(0, 1);
    } else if (expectedProcedures.some((ep) => ep.code === runProcedure.code)) {
      // the case where the run procedure is in the expected list
      // but we are not there yet...
      const expectedProcedureIndex = expectedProcedures.findIndex((ep) => ep.code === runProcedure.code);
      outputValues.push({
        ...runProcedure,
        isExpected: true,
        isPresent: true,
        isPositionCorrect: loopIndex === args.expectedProcedures.findIndex((ep) => ep.code === runProcedure.code),
      });
      expectedProcedures.splice(expectedProcedureIndex, 1);
      runProcedures.splice(0, 1);
    } else if (runProcedures.length > 0) {
      outputValues.push({
        ...runProcedure,
        isExpected: false,
        isPresent: true,
        isPositionCorrect: false,
      });
      runProcedures.splice(0, 1);
    }

    loopIndex++;
  }

  return outputValues;
};

export interface DiagnosisWithDisplayFlags extends Diagnosis {
  isExpected: boolean;
  isPresent: boolean;
  isPositionCorrect: boolean;
  isOptional?: boolean;
}

export const getAllDiagnoses = (args: {
  expectedDiagnoses: (Diagnosis & { isOptional: boolean })[];
  runDiagnoses: Diagnosis[];
}): DiagnosisWithDisplayFlags[] => {
  const expectedDiagnoses = [...args.expectedDiagnoses];
  const runDiagnoses = [...args.runDiagnoses];

  const outputValues = [];
  let loopIndex = 0;

  while (loopIndex < args.expectedDiagnoses.length + args.runDiagnoses.length) {
    const expectedDiagnosis = expectedDiagnoses[0];
    const runDiagnosis = runDiagnoses[0];

    if (!expectedDiagnosis && !runDiagnosis) {
      break;
    }

    if (runDiagnosis && !expectedDiagnosis) {
      outputValues.push({
        ...runDiagnosis,
        isExpected: false,
        isPresent: true,
        isPositionCorrect: false,
      });
      runDiagnoses.splice(0, 1);
    } else if (expectedDiagnosis && !runDiagnosis) {
      outputValues.push({
        ...expectedDiagnosis,
        isExpected: true,
        isPresent: false,
        isPositionCorrect: false,
      });
      expectedDiagnoses.splice(0, 1);
    } else if (expectedDiagnosis.code === runDiagnosis.code) {
      // We can track the original positions by keeping track of how many items we've removed from each list
      const expectedItemsRemoved = args.expectedDiagnoses.length - expectedDiagnoses.length;
      const runItemsRemoved = args.runDiagnoses.length - runDiagnoses.length;

      // The current position in the original lists
      const originalExpectedPosition = expectedItemsRemoved;
      const originalRunPosition = runItemsRemoved;

      outputValues.push({
        ...expectedDiagnosis,
        ...runDiagnosis,
        isExpected: true,
        isPresent: true,
        isPositionCorrect: originalExpectedPosition === originalRunPosition,
      });
      runDiagnoses.splice(0, 1);
      expectedDiagnoses.splice(0, 1);
    } else if (expectedDiagnoses.some((ed) => ed.code === runDiagnosis.code)) {
      // the case where the run procedure is in the expected list
      // but we are not there yet...
      const expectedDiagnosisIndex = expectedDiagnoses.findIndex((ed) => ed.code === runDiagnosis.code);
      const originalExpectedDiagnosisIndex = args.expectedDiagnoses.findIndex((ed) => ed.code === runDiagnosis.code);
      const originalExpectedDiagnosis = args.expectedDiagnoses[originalExpectedDiagnosisIndex];

      outputValues.push({
        ...originalExpectedDiagnosis,
        ...runDiagnosis,
        isExpected: true,
        isPresent: true,
        isPositionCorrect: loopIndex === originalExpectedDiagnosisIndex,
      });
      expectedDiagnoses.splice(expectedDiagnosisIndex, 1);
      runDiagnoses.splice(0, 1);
    } else if (runDiagnoses.length > 0) {
      outputValues.push({
        ...runDiagnosis,
        isExpected: false,
        isPresent: true,
        isPositionCorrect: false,
      });
      runDiagnoses.splice(0, 1);
    }

    loopIndex++;
  }

  return outputValues;
};

export interface CoMorbidityWithDisplayFlags extends Diagnosis {
  isExpected: boolean;
  isPresent: boolean;
  isOptional: boolean;
}

export const getAllCoMorbidities = ({
  expectedCoMorbidities,
  runCoMorbidities,
}: {
  expectedCoMorbidities: (Diagnosis & { isOptional: boolean })[];
  runCoMorbidities: Diagnosis[];
}): CoMorbidityWithDisplayFlags[] => {
  const map = new Map<string, CoMorbidityWithDisplayFlags>();

  for (const comorb of runCoMorbidities) {
    const code = comorb.code;
    if (code && !map.has(code)) {
      map.set(code, {
        ...comorb,
        isExpected: expectedCoMorbidities.some((ec) => ec.code === code),
        isPresent: true,
        isOptional: expectedCoMorbidities.find((ec) => ec.code === code)?.isOptional ?? false,
      });
    }
  }

  // Add expected procedures with isExpected: true
  for (const comorb of expectedCoMorbidities) {
    const code = comorb.code;
    if (code && !map.has(code)) {
      map.set(code, {
        ...comorb,
        isExpected: true,
        isPresent: false,
        isOptional: comorb.isOptional || false,
      });
    }
  }

  // Add expected diagnoses with isExpected: true
  for (const comorb of expectedCoMorbidities) {
    const code = comorb.code;
    if (code && !map.has(code)) {
      map.set(code, {
        ...comorb,
        isExpected: true,
        isPresent: false,
        isOptional: comorb.isOptional || false,
      });
    }
  }

  return Array.from(map.values());
};
